import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { tap, map, debounceTime } from 'rxjs/operators';

import { ContactSummary } from '@features/contacts/models/contactsummary';
import { ContactService } from '@features/contacts/services/contact.service';
import { requireTypeaheadSelection } from '@shared/validators';

import { SelectListOption } from '../form-fields/select-list-option';
import { hasErrors } from '../form-fields/validation';

/**
 * @deprecated  Please use `spark-shared-contact-search` component instead
 */
@Component({
  selector: 'app-contact-search',
  templateUrl: './contact-search.component.html',
  styleUrls: ['./contact-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactSearchComponent),
      multi: true
    }
  ]
})
export class ContactSearchComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() individualsOnly: boolean = true;
  @Input() referrersOnly: boolean = false;
  @Input() label = 'Related person';
  @Input() isDisabled = false;
  @Input() isRequired: boolean = false;
  @Input() showHighlight: boolean = false;
  @Output() onContactSelected = new EventEmitter<ContactSummary>();

  selectedContact: ContactSummary | null = null;
  contacts: ContactSummary[] = [];
  formValueSubscription!: Subscription;
  hasErrors = hasErrors;

  private forwardChange = (_: any) => {};
  forwardTouched = () => {};

  contactControl = new UntypedFormControl(null, [requireTypeaheadSelection()]);
  form: UntypedFormGroup = new UntypedFormGroup({
    contact: this.contactControl
  });

  private getContacts(searchTerm: string) {
    return this.individualsOnly
      ? this.contactService.getPeople(searchTerm)
      : this.contactService.getContacts({ search: searchTerm });
  }

  contacts$: (q: string) => Observable<SelectListOption[]> = (
    searchTerm: string
  ) =>
    (this.referrersOnly
      ? this.contactService.getReferrers(searchTerm)
      : this.getContacts(searchTerm)
    ).pipe(
      tap((contacts) => (this.contacts = contacts)),
      map((contacts) =>
        contacts.map((contact) => {
          return { value: contact.id, label: contact.name };
        })
      ),
      debounceTime(400)
    );

  constructor(private contactService: ContactService) {}

  writeValue(contactSummary: ContactSummary): void {
    this.selectedContact = contactSummary;
    if (this.selectedContact != null)
      this.contactControl.setValue({
        value: this.selectedContact.id,
        label: this.selectedContact.name
      });
  }

  registerOnChange(fn: any): void {
    this.forwardChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.forwardTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnInit() {
    if (this.selectedContact) {
      this.contactControl.setValue({
        value: this.selectedContact.id,
        label: this.selectedContact.name
      });
    }

    this.formValueSubscription = this.contactControl.valueChanges.subscribe(
      (v) => {
        let contact = this.contacts.find((c) => c.id === v.value);
        if (contact != undefined) {
          this.selectedContact = contact;
          this.onContactSelected.emit(contact);
          this.forwardChange(contact);
        }
      }
    );
  }

  ngOnDestroy() {
    this.formValueSubscription?.unsubscribe();
  }
}
